import { useState } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet-async";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { EditIcon, TrashIcon } from "lucide-react";
import {
  Button,
  Loader,
  Tab,
  Tabs,
  useTheme,
} from "@monolith-forensics/monolith-ui";

import { usePermissions } from "@/hooks/usePermissions";
import { useFeatures } from "@/contexts/FeaturesContext";
import AccessDeniedMessage from "@/components/AccessDeniedMessage.js";
import Page404 from "@/pages/auth/Page404";
import { BreadcrumbPageHeader } from "@/components/BreadCrumbHeader";
import {
  AssetAttachmentsSection,
  AssetHistoryFeed,
  AssetValidationsTable,
  CreateValidationModal,
  DeleteLabAssetModal,
  EditLabAssetModal,
  LAB_ASSET_TAB_SLUGS,
  LabAssetContextProvider,
  LabAssetDetailsDisplay,
  LabAssetLoadError,
  LabAssetNotFound,
  ValidationDueBadge,
  getLabAssetDisplayName,
  getValidationDueStatus,
  isLabAssetTabSlug,
  useLabAssetContext,
} from "@/Features";

const LabAssetDetailsContent = styled(
  ({ className }: { className?: string }) => {
    const { asset_uuid, active_tab } = useParams();
    const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
    const theme = useTheme();
    const navigate = useNavigate();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCreateValidation, setShowCreateValidation] = useState(false);

    const labAssetContext = useLabAssetContext();
    if (!labAssetContext) {
      throw new Error("Lab asset context not found");
    }

    // An unknown slug (a stale link to a tab that hasn't shipped, or a typo)
    // would render the header over an empty body; send it to Overview.
    if (!isLabAssetTabSlug(active_tab)) {
      return (
        <Navigate
          to={`/assets/${asset_uuid}/${LAB_ASSET_TAB_SLUGS.OVERVIEW}`}
          replace
        />
      );
    }

    const { data: assetInfo, isPending, isError, refetch } = labAssetContext;

    if (isPending) {
      return <Loader />;
    }

    // isError is any non-404 failure (500, network) — useLabAssetItem throws
    // on those instead of mapping them to null, so a transient failure never
    // renders as "Asset not found". Panel only when there's nothing to show:
    // a failed background refetch keeps the cached record, which react-query
    // retains on error, and useLabAssetItem's queryFn already toasted it.
    if (isError && !assetInfo) {
      return (
        <div className={className}>
          <Helmet title="Asset Details | Error" />
          <LabAssetLoadError onRetry={refetch} />
        </div>
      );
    }

    if (!assetInfo) {
      return (
        <div className={className}>
          <Helmet title="Asset Details | Not Found" />
          <LabAssetNotFound />
        </div>
      );
    }

    // The create modal's documentation dropzone is required, so a user who
    // can create validations but not attachments could never submit it —
    // gate the table's "New Validation" button on both.
    const canCreateValidation =
      hasPermission(MONOLITH_PERMISSIONS.VALIDATION_CREATE) &&
      hasPermission(MONOLITH_PERMISSIONS.ASSET_ATTACHMENT_CREATE);

    const displayName = getLabAssetDisplayName(assetInfo) || "Unnamed Asset";
    const pageName = assetInfo.asset_number
      ? `${displayName} (${assetInfo.asset_number})`
      : displayName;

    return (
      <div className={className}>
        <Helmet
          title={`Asset Details | ${assetInfo.asset_number || displayName}`}
        />
        <div className="header-row">
          <BreadcrumbPageHeader
            sectionName="Assets"
            href="/assets"
            pageName={pageName}
          />
          <ValidationDueBadge
            className="header-badge"
            status={getValidationDueStatus(assetInfo.next_validation_due)}
          />
          {/* Same inline subtle icon buttons as EvidenceActionButton. No
              manual refetch after a save: useUpdateLabAsset invalidates the
              item cache, which LabAssetContextProvider subscribes to. */}
          <div className="header-actions">
            {hasPermission(MONOLITH_PERMISSIONS.ASSET_UPDATE) && (
              <Button
                size="xxs"
                variant="subtle"
                leftSection={
                  <EditIcon size={14} color={theme.palette.primary.main} />
                }
                onClick={() => setShowEditModal(true)}
              >
                Edit
              </Button>
            )}
            {hasPermission(MONOLITH_PERMISSIONS.ASSET_DELETE) && (
              <Button
                size="xxs"
                variant="subtle"
                leftSection={
                  <TrashIcon size={14} color={theme.palette.error.main} />
                }
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </Button>
            )}
          </div>
          {showEditModal && (
            <EditLabAssetModal
              show={showEditModal}
              onClose={() => setShowEditModal(false)}
              defaultInfo={assetInfo}
            />
          )}
          {showDeleteModal && (
            <DeleteLabAssetModal
              show={showDeleteModal}
              onClose={() => setShowDeleteModal(false)}
              onDelete={() => navigate("/assets")}
              defaultInfo={assetInfo}
            />
          )}
          {/* No onSubmit: useCreateAssetValidation invalidates the table's
              infinite list, which AssetValidationsTable refetches. */}
          {showCreateValidation && (
            <CreateValidationModal
              show={showCreateValidation}
              assetUuid={assetInfo.uuid}
              onClose={() => setShowCreateValidation(false)}
            />
          )}
        </div>
        <Tabs orientation="horizontal" value={active_tab}>
          <Link
            to={`/assets/${assetInfo.uuid}/${LAB_ASSET_TAB_SLUGS.OVERVIEW}`}
          >
            <Tab value={LAB_ASSET_TAB_SLUGS.OVERVIEW}>Overview</Tab>
          </Link>
          <Link
            to={`/assets/${assetInfo.uuid}/${LAB_ASSET_TAB_SLUGS.VALIDATIONS}`}
          >
            <Tab value={LAB_ASSET_TAB_SLUGS.VALIDATIONS}>Validations</Tab>
          </Link>
          <Link
            to={`/assets/${assetInfo.uuid}/${LAB_ASSET_TAB_SLUGS.ATTACHMENTS}`}
          >
            <Tab value={LAB_ASSET_TAB_SLUGS.ATTACHMENTS}>Attachments</Tab>
          </Link>
          <Link to={`/assets/${assetInfo.uuid}/${LAB_ASSET_TAB_SLUGS.HISTORY}`}>
            <Tab value={LAB_ASSET_TAB_SLUGS.HISTORY}>History</Tab>
          </Link>
        </Tabs>
        <div className="tab-body">
          {active_tab === LAB_ASSET_TAB_SLUGS.OVERVIEW && (
            <LabAssetDetailsDisplay orientation="horizontal" />
          )}
          {/* Gated in the body, not on the tab link, so the slug stays
              reachable; the api would 403 the list request anyway. */}
          {active_tab === LAB_ASSET_TAB_SLUGS.VALIDATIONS &&
            (hasPermission(MONOLITH_PERMISSIONS.VALIDATION_READ_ALL) ? (
              <AssetValidationsTable
                assetUuid={assetInfo.uuid}
                stateStoreKey="asset-validations:table"
                onCreateClick={
                  canCreateValidation
                    ? () => setShowCreateValidation(true)
                    : undefined
                }
              />
            ) : (
              <AccessDeniedMessage message="You do not have permission to view validations." />
            ))}
          {/* Same body-level gate as Validations. Keyed by asset: the page
              isn't remounted between cached assets, and the section's
              pending uploads belong to the asset they were dropped on. */}
          {active_tab === LAB_ASSET_TAB_SLUGS.ATTACHMENTS &&
            (hasPermission(MONOLITH_PERMISSIONS.ASSET_ATTACHMENT_READ_ALL) ? (
              <AssetAttachmentsSection
                key={assetInfo.uuid}
                assetUuid={assetInfo.uuid}
              />
            ) : (
              <AccessDeniedMessage message="You do not have permission to view attachments." />
            ))}
          {/* No permission gate beyond the page's ASSET_READ_ALL: the api
              serves validation rows in this feed without VALIDATION_READ_ALL. */}
          {active_tab === LAB_ASSET_TAB_SLUGS.HISTORY && (
            <AssetHistoryFeed assetUuid={assetInfo.uuid} />
          )}
        </div>
      </div>
    );
  }
)`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 10px;

  .header-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
  }

  .header-actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    /* BreadcrumbPageHeader carries a 10px bottom margin; match it so the
       buttons center on the title text rather than the header box. */
    margin-bottom: 10px;
  }

  .header-badge {
    margin-bottom: 10px;
  }

  .tab-body {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    margin-top: 5px;
  }
`;

const LabAssetDetails = () => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const { hasFeatures, FEATURES } = useFeatures();
  const { asset_uuid } = useParams();

  if (!hasFeatures([FEATURES.LAB_ASSETS])) {
    return <Page404 />;
  }

  if (!hasPermission(MONOLITH_PERMISSIONS.ASSET_READ_ALL)) {
    return <AccessDeniedMessage />;
  }

  if (!asset_uuid) {
    throw new Error("No asset UUID provided");
  }

  return (
    <LabAssetContextProvider uuid={asset_uuid}>
      <LabAssetDetailsContent />
    </LabAssetContextProvider>
  );
};

export default LabAssetDetails;
